import { Localization } from "@viamap/viamap2-common";

function normalizePages(pages: number[] | undefined): number[] {
   return Array.from(new Set(pages || []))
      .filter((page) => Number.isFinite(page) && page > 0)
      .map((page) => Math.trunc(page))
      .sort((a, b) => a - b);
}

function arePagesEqual(a: number[], b: number[]): boolean {
   const left = normalizePages(a);
   const right = normalizePages(b);
   return left.length === right.length && left.every((page, index) => page === right[index]);
}

export type ServitutDocumentSourceKind = "akt" | "file";
export type ServitutDocumentOriginKind = "ai" | "person";
export type ServitutReviewState = "unchecked" | "checked" | "rejected";
export type ServitutPageFindStatus = "found" | "none" | "not_run";
export type ServitutAiFieldStatus =
   | "not_run"
   | "cannot_run"
   | "ai_glad"
   | "ai_ikke_glad"
   | "person_approved";

export type ServitutAiFieldKey = "resume" | "entitledParty" | "condition" | "statement" | "pages";

export interface ANNServitutFieldStatusMap {
   resume?: ServitutAiFieldStatus;
   entitledParty?: ServitutAiFieldStatus;
   condition?: ServitutAiFieldStatus;
   statement?: ServitutAiFieldStatus;
   pages?: ServitutAiFieldStatus;
}

export type ServitutAktUsedSource = "korrektAkt" | "servitutAkt" | "textAkt" | "ovrigAkt" | "customAkt";

/** Compact provenance stored on the servitut document annotation. */
export interface ANNServitutProvenance {
   source?: ServitutDocumentSourceKind;
   origin?: ServitutDocumentOriginKind;
   reviewState?: ServitutReviewState;
   aiPagesKept?: number[];
   fieldStatus?: ANNServitutFieldStatusMap;
   analysisStale?: boolean;
   /** Akt the saved PDF was cut from. Not the Fortryd / land-registry fallback. */
   aktId?: string;
   usedSource?: ServitutAktUsedSource;
}

export function emptyProvenance(): ANNServitutProvenance {
   return {
      reviewState: "unchecked",
      fieldStatus: {},
      analysisStale: false,
   };
}

export function mergeProvenance(
   previous: ANNServitutProvenance | undefined,
   patch: Partial<ANNServitutProvenance>,
): ANNServitutProvenance {
   return {
      ...emptyProvenance(),
      ...previous,
      ...patch,
      fieldStatus: {
         ...(previous?.fieldStatus || {}),
         ...(patch.fieldStatus || {}),
      },
   };
}

export function provenanceAfterDocumentChange(
   previous: ANNServitutProvenance | undefined,
   patch: Partial<ANNServitutProvenance>,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      ...patch,
      reviewState: "unchecked",
      analysisStale: true,
      fieldStatus: {
         resume: "not_run",
         entitledParty: "not_run",
         condition: "not_run",
         statement: "not_run",
         pages: patch.fieldStatus?.pages || "not_run",
      },
   });
}

export function provenanceAfterAiAnalysis(
   previous: ANNServitutProvenance | undefined,
   options: {
      aiPagesKept: number[];
      pageFindStatus: ServitutPageFindStatus;
      resumeStatus: ServitutAiFieldStatus;
      entitledPartyStatus: ServitutAiFieldStatus;
      conditionStatus: ServitutAiFieldStatus;
      statementStatus: ServitutAiFieldStatus;
   },
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      origin: "ai",
      reviewState: "unchecked",
      analysisStale: false,
      aiPagesKept: normalizePages(options.aiPagesKept),
      fieldStatus: {
         pages: options.pageFindStatus === "found"
            ? options.resumeStatus
            : options.pageFindStatus === "none"
               ? "ai_ikke_glad"
               : "not_run",
         resume: options.resumeStatus,
         entitledParty: options.entitledPartyStatus,
         condition: options.conditionStatus,
         statement: options.statementStatus,
      },
   });
}

export function provenanceAfterLocate(
   previous: ANNServitutProvenance | undefined,
   options: {
      aiPagesKept: number[];
      pageFindStatus: ServitutPageFindStatus;
      pagesStatus?: ServitutAiFieldStatus;
      analysisStale?: boolean;
   },
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      origin: "ai",
      reviewState: "unchecked",
      analysisStale: options.analysisStale ?? false,
      aiPagesKept: normalizePages(options.aiPagesKept),
      fieldStatus: {
         pages: options.pageFindStatus === "found"
            ? (options.pagesStatus || "ai_glad")
            : options.pageFindStatus === "none"
               ? "ai_ikke_glad"
               : "not_run",
      },
   });
}

export function provenanceAfterContentAnalysis(
   previous: ANNServitutProvenance | undefined,
   options: {
      resumeStatus: ServitutAiFieldStatus;
      entitledPartyStatus: ServitutAiFieldStatus;
      conditionStatus: ServitutAiFieldStatus;
      statementStatus: ServitutAiFieldStatus;
   },
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      analysisStale: false,
      fieldStatus: {
         resume: options.resumeStatus,
         entitledParty: options.entitledPartyStatus,
         condition: options.conditionStatus,
         statement: options.statementStatus,
      },
   });
}

export function provenanceAfterFieldAccept(
   previous: ANNServitutProvenance | undefined,
   field: Exclude<ServitutAiFieldKey, "pages">,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      analysisStale: false,
      fieldStatus: {
         [field]: "person_approved",
      },
   });
}

/** Undo a B-field accept without touching A page review. */
export function provenanceAfterFieldRevert(
   previous: ANNServitutProvenance | undefined,
   field: Exclude<ServitutAiFieldKey, "pages">,
   restoredStatus?: ServitutAiFieldStatus,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      fieldStatus: {
         [field]: restoredStatus || "not_run",
      },
   });
}

export function provenanceAfterDerivedInvalidation(
   previous: ANNServitutProvenance | undefined,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      analysisStale: true,
      fieldStatus: {
         resume: "not_run",
         entitledParty: "not_run",
         condition: "not_run",
         statement: "not_run",
         pages: previous?.fieldStatus?.pages,
      },
   });
}

export function provenanceAfterPageRevert(
   previous: ANNServitutProvenance | undefined,
): ANNServitutProvenance {
   const previousPages = previous?.fieldStatus?.pages;
   const restoredPages = previousPages === "person_approved"
      ? (previous?.aiPagesKept?.length ? "ai_glad" : "not_run")
      : previousPages;
   return mergeProvenance(previous, {
      reviewState: "unchecked",
      fieldStatus: {
         pages: restoredPages,
      },
   });
}

/** Phase A page control: mark pages checked/rejected without approving B analysis fields. */
export function provenanceAfterPageReview(
   previous: ANNServitutProvenance | undefined,
   reviewState: "checked" | "rejected",
   origin: ServitutDocumentOriginKind,
): ANNServitutProvenance {
   return mergeProvenance(previous, {
      reviewState,
      origin,
      fieldStatus: {
         pages: reviewState === "checked" ? "person_approved" : previous?.fieldStatus?.pages,
      },
   });
}

export function historyCommentForProvenance(provenance: ANNServitutProvenance): string {
   const parts = [
      provenance.source === "file"
         ? Localization.getText("Loaded from file")
         : provenance.source === "akt"
            ? Localization.getText("Fetched from akt")
            : "",
      provenance.origin === "ai"
         ? Localization.getText("AI generated")
         : provenance.origin === "person"
            ? Localization.getText("Edited by person")
            : "",
      provenance.reviewState === "checked"
         ? Localization.getText("Approved")
         : provenance.reviewState === "rejected"
            ? Localization.getText("Rejected")
            : "",
   ].filter(Boolean);
   return parts.join(" · ");
}

export function statusFromConfidence(
   value: number | null | undefined,
   needsReview?: boolean,
   hasValue?: boolean,
   canRun = true,
): ServitutAiFieldStatus {
   if (!canRun) {
      return "cannot_run";
   }
   // A locate-pass confidence score is not enough. B-fields stay "not run"
   // until the content analysis actually produced a value for that field.
   if (!hasValue) {
      return "not_run";
   }
   if (needsReview) {
      return "ai_ikke_glad";
   }
   if (value === null || value === undefined) {
      return "ai_glad";
   }
   const normalized = value > 1 ? value / 10 : value;
   if (normalized >= 0.85) {
      return "ai_glad";
   }
   return "ai_ikke_glad";
}

/** Same explanation used for the yellow "AI found pages, not confident" chip. */
export function getPageFindReason(analysisItem: {
   overall_confidence_reasoning?: string;
   review_reasons?: string[];
   reasoning?: string;
} | undefined): string | undefined {
   if (!analysisItem) {
      return undefined;
   }
   const fromReview = Array.isArray(analysisItem.review_reasons)
      ? analysisItem.review_reasons.filter(Boolean).join(" · ")
      : "";
   const reason = (
      analysisItem.overall_confidence_reasoning
      || fromReview
      || analysisItem.reasoning
      || ""
   ).toString().trim();
   return reason || undefined;
}

/** Page-find confidence when the model returned a page list. */
export function pageFindConfidenceStatus(
   analysisItem: {
      confidence?: number;
      overall_confidence?: number;
      needs_review?: boolean;
   } | undefined,
   pagesKept: number[],
): ServitutAiFieldStatus {
   if (!pagesKept.length) {
      return "ai_ikke_glad";
   }
   return statusFromConfidence(
      analysisItem?.overall_confidence ?? analysisItem?.confidence,
      analysisItem?.needs_review,
      true,
   );
}

export function resolvePageFindStatus(options: {
   electronicDocument: boolean;
   hasAnalysis: boolean;
   pagesKept: number[];
}): ServitutPageFindStatus {
   if (options.electronicDocument) {
      return "found";
   }
   if (!options.hasAnalysis) {
      return "not_run";
   }
   return options.pagesKept.length > 0 ? "found" : "none";
}

export function areServitutPagesEqual(a: number[] | undefined, b: number[] | undefined): boolean {
   return arePagesEqual(a || [], b || []);
}

export function pagesDifferFromAi(selectedPages: number[], aiPagesKept: number[] | undefined, electronicDocument: boolean): boolean {
   if (electronicDocument) {
      return false;
   }
   if (!aiPagesKept || !aiPagesKept.length) {
      return selectedPages.length > 0;
   }
   return !arePagesEqual(selectedPages, aiPagesKept);
}

export function formatAiFieldStatus(status: ServitutAiFieldStatus | undefined): string {
   switch (status) {
      case "cannot_run":
         return Localization.getText("AI cannot run");
      case "ai_glad":
      case "ai_ikke_glad":
         return Localization.getText("AI");
      case "person_approved":
         return Localization.getText("Approved by person");
      case "not_run":
      default:
         return Localization.getText("AI not run");
   }
}

export function formatProvenanceChip(
   provenance: ANNServitutProvenance | undefined,
   options?: { hideAiGenerated?: boolean; hideFetchedFromAkt?: boolean },
): string {
   const parts = [
      provenance?.source === "file"
         ? Localization.getText("Loaded from file")
         : provenance?.source === "akt"
            ? (options?.hideFetchedFromAkt ? "" : Localization.getText("Fetched from akt"))
            : "",
      provenance?.origin === "ai"
         ? (options?.hideAiGenerated ? "" : Localization.getText("AI generated"))
         : provenance?.origin === "person"
            ? Localization.getText("Edited by person")
            : "",
      provenance?.reviewState === "checked"
         ? Localization.getText("Approved")
         : provenance?.reviewState === "rejected"
            ? Localization.getText("Rejected")
            : "",
      provenance?.analysisStale
         ? Localization.getText("Analysis outdated")
         : "",
   ].filter(Boolean);
   return parts.join(" · ");
}

export function provenanceChipTone(provenance: ANNServitutProvenance | undefined): "ok" | "warn" | "bad" | "neutral" {
   if (provenance?.reviewState === "rejected" || provenance?.analysisStale) {
      return "bad";
   }
   if (provenance?.reviewState === "checked") {
      return "ok";
   }
   if (provenance?.origin === "ai") {
      return "warn";
   }
   return "neutral";
}

export function aiFieldTone(status: ServitutAiFieldStatus | undefined): "ok" | "warn" | "bad" | "neutral" {
   switch (status) {
      case "ai_glad":
      case "person_approved":
         return "ok";
      case "ai_ikke_glad":
         return "warn";
      case "cannot_run":
         return "bad";
      default:
         return "neutral";
   }
}

export function normalizeDatoLoebenrKey(value?: string | null): string {
   return (value || "").replace(/[^0-9]/g, "");
}
